import { useEffect, useState } from 'react'
import type { AdminSectionProps } from './types'
import { db } from '@core/db/db'
import { pluralRuCount } from '@core/utils/plural'
import { CloudReceiptsSection } from './CloudReceiptsSection'
import { SyncQueueSection } from './SyncQueueSection'
import { ContainersMonitorSection } from './ContainersMonitorSection'
import { WriteoffsSection } from './WriteoffsSection'
import { ShiftsMonitorSection } from './ShiftsMonitorSection'
import { AdminCard } from './AdminCard'
import { useAuthToken } from './useAuthToken'
import { btn, btnBase, btnSize, cx } from '@ui/classes'

interface SalesCounts {
  receipts: number
  openShifts: number
  queue: number
  writeoffs: number
  containers: number
}

/** Раздел «Продажи»: чеки в облаке, смены/Z-отчёты и вскрытые упаковки. */
export function SalesSection({ perms }: AdminSectionProps) {
  const token = useAuthToken()
  // Одна кнопка «Обновить» на весь раздел: секции перезагружаются по refreshKey.
  const [refreshKey, setRefreshKey] = useState(0)
  const [counts, setCounts] = useState<SalesCounts>({
    receipts: 0,
    openShifts: 0,
    queue: 0,
    writeoffs: 0,
    containers: 0
  })

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const [receipts, openShifts, queueRows, writeoffs, containers] = await Promise.all([
        db.receipts.count(),
        db.shifts.where('status').equals('open').count(),
        db.sales_queue.toArray(),
        db.writeoffs.count(),
        db.opened_containers.where('status').equals('active').count()
      ])
      if (cancelled) return
      setCounts({
        receipts,
        openShifts,
        queue: queueRows.filter((row) => !row.is_synced).length,
        writeoffs,
        containers
      })
    })().catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [refreshKey])

  const meta = (text: string) => (
    <span className="text-small font-normal text-muted">{text}</span>
  )

  return (
    <>
      <div className="mb-3 flex items-center justify-between gap-2">
        <span className="text-small text-muted">Чеки, смены и упаковки</span>
        <button
          className={cx(btnBase, btn.secondary, btnSize.sm)}
          onClick={() => setRefreshKey((k) => k + 1)}
        >
          ⟳ Обновить
        </button>
      </div>

      <AdminCard
        id="sales.receipts"
        title="Чеки в облаке"
        defaultOpen
        meta={meta(pluralRuCount(counts.receipts, ['чек', 'чека', 'чеков']))}
      >
        <CloudReceiptsSection
          refreshKey={refreshKey}
          canApproveMarkingHold={perms.canApproveMarkingHold}
          canWriteoff={perms.canWriteoff}
        />
      </AdminCard>

      <AdminCard
        id="sales.shifts"
        title="Смены и Z-отчёты"
        meta={counts.openShifts > 0 ? meta(`открыто: ${counts.openShifts}`) : undefined}
      >
        <ShiftsMonitorSection token={token} refreshKey={refreshKey} />
      </AdminCard>

      <AdminCard
        id="sales.sync-queue"
        title="Очередь синхронизации"
        meta={counts.queue > 0 ? meta(`в очереди: ${counts.queue}`) : undefined}
      >
        <SyncQueueSection refreshKey={refreshKey} />
      </AdminCard>

      <AdminCard
        id="sales.writeoffs"
        title="Списания"
        meta={meta(pluralRuCount(counts.writeoffs, ['списание', 'списания', 'списаний']))}
      >
        <WriteoffsSection refreshKey={refreshKey} />
      </AdminCard>

      <AdminCard
        id="sales.containers"
        title="Вскрытые упаковки"
        meta={counts.containers > 0 ? meta(`активных: ${counts.containers}`) : undefined}
      >
        <ContainersMonitorSection token={token} refreshKey={refreshKey} />
      </AdminCard>
    </>
  )
}
